import { hexToRgba } from "utils/data";
import { MainAxesName } from "shared/plotly/types";
import { AXES_REGEXP, CONFIDENCE_RANGE, DRAGS_REGEXP, RestyleYAxesDirection, } from "./types";
import * as colors from "styles/colorValues.module.scss";
import { convertToNumber } from "utils/string";
import { isMergedAxis, MAX_VISIBLE_AXES } from "ps-components/Plot/utils";
import { defaultAxisConfig } from "pages/RS-Workspace/components/Results/components/Elements/components/GraphElementGraph/utils";
export const getLayoutAxesRanges = (plotLayout, axesRangesConfig) => {
    return Object.keys(plotLayout)
        .filter((key) => AXES_REGEXP.test(key))
        .reduce((obj, key) => {
        const { autorange, responseId, range, showgrid, fixedrange } = {
            ...plotLayout[key],
        };
        const min = convertToNumber(range && range[0], axesRangesConfig[key]?.min);
        const max = convertToNumber(range && range[1], axesRangesConfig[key]?.max);
        return {
            ...obj,
            [key]: {
                ...(responseId && { responseId }),
                autorange,
                fixedrange,
                min,
                max,
                showgrid,
            },
        };
    }, {});
};
export const checkIsAllAxesAutoscaled = (layout) => Object.keys(layout)
    .filter((key) => AXES_REGEXP.test(key))
    .every((key) => {
    const { autorange, isConfidence, range } = { ...layout[key] };
    return (!!autorange ||
        (isConfidence && range?.[0] === CONFIDENCE_RANGE[0] && range?.[1] === CONFIDENCE_RANGE[1]));
});
export const checkIsAllGridlinesVisible = (layout) => Object.keys(layout)
    .filter((key) => AXES_REGEXP.test(key))
    .every((key) => {
    const { showgrid } = { ...layout[key] };
    return showgrid;
});
const getRestyleYAxesTransform = (axesDirection, visibleYAxes, i) => {
    switch (axesDirection) {
        case RestyleYAxesDirection.ALL_LEFT:
            return {
                layer: `translateX(calc((${visibleYAxes.length} * -45px) - (${i + 1} * -45px)))`,
                dragLayer: `translateX(calc(((${visibleYAxes.length} * -45px) - (${i + 1} * -45px)) + -24px))`,
            };
        case RestyleYAxesDirection.THREE_LEFT_THREE_RIGHT: {
            const isThirdLastAxis = i === 2 && visibleYAxes.length < 4;
            return {
                layer: i < 3
                    ? isThirdLastAxis
                        ? `translateX(0)`
                        : `translateX(calc((${visibleYAxes.slice(0, 3).length * -55 -
                            (visibleYAxes.length !== 3 ? (i + 1) * -55 : (i + 2) * -55)}px)))`
                    : `translateX(calc(((${visibleYAxes.slice(4, visibleYAxes.length).length} * -55px) - (${i - (1 + MAX_VISIBLE_AXES - visibleYAxes.length)} * -55px))`,
                dragLayer: i < 3
                    ? isThirdLastAxis
                        ? `translateX(0)`
                        : `translateX(calc((${visibleYAxes.slice(0, 3).length * -55 -
                            (visibleYAxes.length !== 3 ? (i + 1) * -55 : (i + 2) * -55) +
                            -34}px)))`
                    : `translateX(calc(((${visibleYAxes.slice(4, visibleYAxes.length).length} * -55px) - (${i - (1 + MAX_VISIBLE_AXES - visibleYAxes.length)} * -55px))))`,
            };
        }
    }
};
export const handleRestyleYAxes = (axesDirection, layout, graphDiv, axesRangesConfig, setAxesRangesConfig) => {
    const visibleYAxes = Object.keys(layout).filter((key) => AXES_REGEXP.test(key) && layout[key]?.visible);
    const cartesianLayer = graphDiv?.querySelector(".cartesianlayer");
    const dragLayer = graphDiv?.querySelector(".draglayer");
    if (cartesianLayer || dragLayer) {
        visibleYAxes.forEach((key, i) => {
            const domKey = key.replace("axis", "");
            const axisLayerSelector = domKey === "y" ? ".yaxislayer-above" : `.overaxes-above .x${domKey}-y`;
            const axisLineSelector = domKey === "y" ? ".ylines-above" : `.overlines-above .x${domKey}-y`;
            const yDragControlsSelector = `.x${domKey} .drag:not([nsewdrag])`;
            const transform = getRestyleYAxesTransform(axesDirection, visibleYAxes, i);
            let shouldResetRanges = false;
            visibleYAxes
                .concat(MainAxesName.X_AXIS)
                .filter((key) => AXES_REGEXP.test(key))
                .map((key) => {
                const axis = layout[key];
                if (axis.responseId && isMergedAxis(axis.responseId, layout)) {
                    axis.gridcolor = "transparent";
                }
                else {
                    axis.gridcolor = defaultAxisConfig.gridcolor;
                }
                const { range } = {
                    ...axis,
                };
                const min = range && convertToNumber(range[0]);
                const max = range && convertToNumber(range[1]);
                if (min !== axesRangesConfig[key]?.min ||
                    max !== axesRangesConfig[key]?.max) {
                    shouldResetRanges = true;
                }
            }, {});
            if (shouldResetRanges) {
                const layoutAxesRanges = getLayoutAxesRanges(layout, axesRangesConfig);
                setAxesRangesConfig(layoutAxesRanges);
            }
            if (cartesianLayer) {
                const axisLayer = cartesianLayer.querySelector(axisLayerSelector);
                const axisLine = cartesianLayer.querySelector(axisLineSelector);
                if (axisLayer && axisLine) {
                    [axisLine, axisLayer].forEach((el) => (el.style.transform = transform.layer));
                }
            }
            if (dragLayer) {
                const yDragControls = dragLayer.querySelectorAll(yDragControlsSelector);
                const filteredYDragControls = [...yDragControls].filter((el) => [...el.classList].some((className) => DRAGS_REGEXP.test(className)));
                filteredYDragControls.forEach((el) => (el.style.transform = transform.dragLayer));
            }
        });
    }
};
export const getTraceTemplate = (index, type, color, visible) => {
    const baseConfig = {
        type: "scatter",
        visible,
        yaxis: `y${index > 0 ? index + 1 : ""}`,
        y: [],
        x: [],
        customdata: [],
    };
    const linesBaseConfig = {
        mode: "lines",
        ...baseConfig,
    };
    switch (type) {
        case "lines":
            return {
                ...linesBaseConfig,
                text: [],
                hovertemplate: "%{text}<extra></extra>",
                line: { color: hexToRgba(color), width: 3 },
            };
        case "lines+spread":
            return {
                ...linesBaseConfig,
                fill: "toself",
                hoverinfo: "skip",
                fillcolor: hexToRgba(color, 0.1),
                line: { color: hexToRgba(color, 0.3), width: 3 },
            };
        case "discrete":
            return {
                ...baseConfig,
                mode: "markers",
                hovertemplate: "%{text}<extra></extra>",
                text: [],
                marker: { color: hexToRgba(color) },
            };
    }
};
export const getErrorsTrace = (isLineGraph) => {
    if (isLineGraph === undefined) {
        return {};
    }
    return isLineGraph
        ? {
            x: [],
            y: [],
            type: "bar",
            opacity: 0.1,
            marker: {
                color: colors.red,
            },
            hoverinfo: "skip",
            yaxis: "y2",
        }
        : {
            mode: "lines",
            type: "scatter",
            yaxis: "y2",
            x: [],
            y: [],
            text: [],
            hoverinfo: "skip",
            line: { color: colors.red, width: 3 },
        };
};
